import { useState, useEffect, useMemo, useRef } from 'react';
import { holdAppUpdate } from '@app-update';
import { apiFetch } from '../../utils/api';
import TemplatePicker from '../../components/TemplatePicker';
import { Toolbar, Sidebar, CanvasArea, StylingPanel, ConfiguratorPreviewLayout } from './components';
import {
    DEFAULT_DESIGN_SETTINGS,
    DEFAULT_EMBED_SETTINGS,
    DEFAULT_LAYOUT_MAPPING,
    DEFAULT_STYLE_TOKENS,
    DEFAULT_THEME,
    applyStyleTweaksToCss,
    buildThemeFromTokens,
    describeStyleTweaks,
    extractStyleTokensFromCss,
    getEmbedPageBackground,
    inferStyleTweaksFromPrompt,
    isRevertStylePrompt,
    normalizeDesignSettings,
    normalizeEmbedSettings,
    normalizeTheme,
    parseNumber,
    getFlowMode
} from './utils';
import { selectionsFromDefaultValues } from '../../utils/featureNormalization';
import { countDesignOverrides, mergeFeaturePresentation } from '../../utils/presentationCatalog';
import { deriveSystemSelections, displaySelections } from '../../utils/systemSelections';

function designSnapshot(pages, properties, presentationOverrides) {
    return JSON.stringify({
        pages,
        properties,
        presentationOverrides
    });
}

const colorInputValue = (value, fallback) => /^#[0-9a-f]{6}$/i.test(value || '') ? value : fallback;

const SHADOW_CONTROL_OPTIONS = [
    { label: 'None', value: 'none' },
    { label: 'Subtle', value: '0 1px 2px rgba(0,0,0,0.06)' },
    { label: 'Soft', value: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)' },
    { label: 'Medium', value: '0 10px 15px -3px rgba(0,0,0,0.12), 0 4px 6px -4px rgba(0,0,0,0.08)' },
    { label: 'Strong', value: '0 18px 28px -8px rgba(0,0,0,0.22), 0 8px 12px -8px rgba(0,0,0,0.12)' }
];

export default function GuiBuilder({ projectId, linkedFamilies, versionTag: initialVersionTag = '1.0.0', familyId, familyName, onLayoutSaved }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sourceFeatures, setSourceFeatures] = useState([]);
    const [catalogContent, setCatalogContent] = useState({ features: {} });
    const [catalogDirty, setCatalogDirty] = useState(false);
    const [presentationOverrides, setPresentationOverrides] = useState({});
    const [presentationSetId, setPresentationSetId] = useState(null);
    const features = useMemo(
        () => sourceFeatures.map(feature => mergeFeaturePresentation(feature, catalogContent, presentationOverrides)),
        [sourceFeatures, catalogContent, presentationOverrides]
    );

    useEffect(() => {
        if (!catalogDirty || !projectId || !presentationSetId) return undefined;
        const handle = setTimeout(async () => {
            try {
                const res = await apiFetch(`/api/projects/${projectId}/presentation-sets/${presentationSetId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ content: catalogContent })
                });
                if (res.ok) setCatalogDirty(false);
            } catch (err) {
                console.error('Failed to save GUI data from the designer', err);
            }
        }, 500);
        return () => clearTimeout(handle);
    }, [catalogDirty, catalogContent, projectId, presentationSetId]);
    
    // Multi-page Layout Schema State
    const [pages, setPages] = useState([
        {
            id: 'page-1',
            title: 'Base Specifications',
            description: 'Configure the core properties of your configuration.',
            elements: []
        }
    ]);
    const [activePageIndex, setActivePageIndex] = useState(0);
    const [versionTag, setVersionTag] = useState(initialVersionTag);
    const [isPublished, setIsPublished] = useState(false);
    
    // UI state
    const [designerMode, setDesignerMode] = useState('edit'); // edit, view
    const [openSidebarSections, setOpenSidebarSections] = useState([]); // modules, elements, palette, layout, ai-design
    const toggleSidebarSection = (id) => {
        setOpenSidebarSections(prev => (
            prev.includes(id) ? prev.filter(sectionId => sectionId !== id) : [...prev, id]
        ));
    };
    const [activeStylingTab, setActiveStylingTab] = useState('controls'); // controls, css, templates
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);
    const [isDragOverCanvas, setIsDragOverCanvas] = useState(false);
    
    // Preview Mode Navigation State
    const [previewPageIndex, setPreviewPageIndex] = useState(0);
    const [previewSelections, setPreviewSelections] = useState({});
    const [previewAvailableDomains, setPreviewAvailableDomains] = useState(null);
    const [showIntroFrontPage, setShowIntroFrontPage] = useState(true);

    // Smart Designer State
    const [targetUrl, setTargetUrl] = useState('');
    const [aiPrompt, setAiPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationStatus, setGenerationStatus] = useState('');
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [generatedPayload, setGeneratedPayload] = useState(null);
    const [modalViewMode, setModalViewMode] = useState('css'); // css, preview
    const [chatMessages, setChatMessages] = useState([]);
    const [chatLoading, setChatLoading] = useState(false);
    const [chatInput, setChatInput] = useState('');
    const chatEndRef = useRef(null);

    // Visual Tweaker States
    const [modalSidebarTab, setModalSidebarTab] = useState('chat'); // chat, manual
    const [manualStyles, setManualStyles] = useState(DEFAULT_STYLE_TOKENS);
    const styleHistoryRef = useRef([]);


    // Advanced Page Layout properties
    const [properties, setProperties] = useState({
        layoutTemplate: 'split-70-30', // split-70-30, split-50-50, split-30-70, single-column
        layoutMapping: {
            column1: ['features-view', 'wizard-navigation'],
            column2: ['selections-overview']
        },
        designSettings: DEFAULT_DESIGN_SETTINGS,
        embed: DEFAULT_EMBED_SETTINGS,
        theme: DEFAULT_THEME,
        customCss: `/* Custom stylesheet overrides for the configurator */
.cfg-header {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--accent, #001e62);
  margin-bottom: 0.5rem;
}

.cfg-description {
  font-size: 0.95rem;
  color: var(--text-secondary, #4a5568);
  line-height: 1.6;
  margin-bottom: 2rem;
}

.cfg-feature-group {
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cfg-feature-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-secondary, #4a5568);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.cfg-input-control {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border, #cbd5e0);
  border-radius: 4px;
  background-color: var(--bg-card, #ffffff);
  color: var(--text-primary, #1a202c);
  font-size: 0.9rem;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.cfg-select-input {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%234a5568' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 40px;
}

.cfg-no-select-chrome {
  appearance: auto !important;
  background-image: none !important;
}

.cfg-input-control label,
.cfg-input-control span,
.cfg-input-control option,
.cfg-select-input label,
.cfg-select-input span,
.cfg-select-input option {
  color: var(--text-primary, #1a202c);
}

.cfg-input-control input[type="radio"],
.cfg-input-control input[type="checkbox"],
.cfg-select-input input[type="radio"],
.cfg-select-input input[type="checkbox"] {
  accent-color: var(--accent, #001e62);
}

.cfg-input-control:focus,
.cfg-input-control:focus-within,
.cfg-select-input:focus {
  border-color: var(--accent, #001e62);
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 30, 98, 0.1);
}

.cfg-next-btn {
  background: var(--accent, #001e62);
  color: #ffffff;
  padding: 14px 28px;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06);
  transition: background-color 0.2s, box-shadow 0.2s;
}

.cfg-next-btn:hover {
  background: var(--accent-hover, #002d62);
  box-shadow: 0 6px 8px -1px rgba(0,0,0,0.15), 0 3px 6px -1px rgba(0,0,0,0.08);
}

.cfg-back-btn {
  background: transparent;
  color: var(--accent, #001e62);
  border: 1px solid var(--accent, #001e62);
  padding: 14px 28px;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: background-color 0.2s;
}

.cfg-back-btn:hover {
  background: rgba(0, 30, 98, 0.05);
}

.cfg-overview-card {
  background-color: var(--bg-secondary, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 4px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
}

.cfg-canvas {
  background-color: #ffffff;
}`
    });

    const savedDesignRef = useRef(null);
    const currentDesign = useMemo(
        () => designSnapshot(pages, properties, presentationOverrides),
        [pages, properties, presentationOverrides]
    );

    useEffect(() => {
        if (loading) {
            savedDesignRef.current = null;
            return;
        }
        if (savedDesignRef.current == null) {
            savedDesignRef.current = currentDesign;
        }
    }, [loading, currentDesign]);

    const designChanged = !loading
        && savedDesignRef.current != null
        && currentDesign !== savedDesignRef.current;

    useEffect(() => {
        if (!designChanged && !catalogDirty) return undefined;
        return holdAppUpdate();
    }, [designChanged, catalogDirty]);

    const pollingTimeoutRef = useRef(null);

    const handleTemplateSelected = async (template) => {
        // Close the template picker
        setShowTemplatePicker(false);
        
        if (template === null) {
            // Start from scratch - reset to empty layout
            setPages([
                {
                    id: 'page-1',
                    title: 'Configuration',
                    description: 'Main configuration page',
                    elements: []
                }
            ]);
            setActivePageIndex(0);
            setProperties(prev => ({
                layoutTemplate: 'split-70-30',
                layoutMapping: {
                    column1: ['features-view', 'wizard-navigation'],
                    column2: ['selections-overview']
                },
                designSettings: DEFAULT_DESIGN_SETTINGS,
                customCss: prev.customCss,
                embed: prev.embed || DEFAULT_EMBED_SETTINGS,
                theme: prev.theme || DEFAULT_THEME
            }));
            setDesignerMode('edit');
            return;
        }

        // Reload the project data to get the updated schema
        await loadData();
    };

    const handleFeatureSelectedFromPalette = (feature) => {
        // Add the selected feature to the current page
        const allPlacedNames = pages.flatMap(p => p.elements)
            .filter(el => el.type === 'variable' || el.type === 'feature')
            .map(el => el.featureName || el.variableName);

        if (allPlacedNames.includes(feature.name)) {
            alert('This feature is already on the canvas layout.');
            return;
        }

        const newEl = {
            id: 'el-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
            type: 'feature',
            featureName: feature.name,
            displayText: feature.displayText || feature.name,
            inputType: feature.inputType,
            isOptional: false
        };
        const updated = [...pages];
        updated[activePageIndex].elements.push(newEl);
        setPages(updated);
    };

    const handleFeatureDraggedFromPalette = (e) => {
        e.preventDefault();
        setIsDragOverCanvas(false);
        const data = e.dataTransfer.getData('text/plain');
        try {
            const featureData = JSON.parse(data);
            if (featureData.type === 'feature') {
                handleFeatureSelectedFromPalette({
                    name: featureData.featureName,
                    displayText: featureData.displayText,
                    inputType: featureData.inputType
                });
            }
        } catch (err) {
            console.error('Error parsing dragged feature data:', err);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOverCanvas(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOverCanvas(false);
    };

    const loadData = async () => {
        setLoading(true);
        try {
            // 1. Fetch layout schema for project with the specific version tag
            const schemaRes = await apiFetch(`/api/projects/${projectId}/gui-schema?versionTag=${initialVersionTag}`);
            if (schemaRes.ok) {
                const schema = await schemaRes.json();
                setVersionTag(schema.versionTag || initialVersionTag);
                setIsPublished(schema.isPublished || false);
                
                // Migrate legacy schemas and unwrap template payloads saved in the wrong shape.
                const designPayload = schema.designPayload || {};
                setPresentationOverrides(designPayload.presentationOverrides || {});
                setPresentationSetId(schema.presentationSetId || null);
                const layoutPayload = Array.isArray(designPayload.pages)
                    ? designPayload
                    : (Array.isArray(designPayload.pages?.pages) ? designPayload.pages : designPayload);
                let loadedPages = [];
                if (Array.isArray(layoutPayload.pages)) {
                    loadedPages = layoutPayload.pages;
                } else if (Array.isArray(designPayload.elements)) {
                    loadedPages = [
                        {
                            id: 'page-legacy',
                            title: 'Base specifications',
                            description: 'Configure your spec baseline.',
                            elements: designPayload.elements
                        }
                    ];
                } else {
                    loadedPages = [
                        {
                            id: 'page-1',
                            title: 'Page 1',
                            description: 'Configuration options',
                            elements: []
                        }
                    ];
                }
                setPages(loadedPages);
                setActivePageIndex(0);

                const loadedProps = designPayload.properties || layoutPayload.properties;
                if (loadedProps && !Array.isArray(loadedProps)) {
                    const normalizedDesignSettings = normalizeDesignSettings(loadedProps.designSettings);

                    setProperties(prev => {
                        const customCss = loadedProps.customCss || prev.customCss;
                        const normalizedEmbed = normalizeEmbedSettings(loadedProps.embed);
                        const styleTokens = extractStyleTokensFromCss(customCss);

                        return {
                            ...prev,
                            ...loadedProps,
                            layoutTemplate: loadedProps.layoutTemplate || 'split-70-30',
                            layoutMapping: loadedProps.layoutMapping || DEFAULT_LAYOUT_MAPPING,
                            customCss,
                            designSettings: normalizedDesignSettings,
                            embed: normalizedEmbed,
                            theme: normalizeTheme(loadedProps.theme, normalizedEmbed, styleTokens)
                        };
                    });
                    setDesignerMode('edit');
                } else {
                    setProperties(prev => ({
                        ...prev,
                        designSettings: normalizeDesignSettings(prev.designSettings)
                    }));
                    setDesignerMode('edit');
                }
            } else {
                setDesignerMode('edit');
            }

            // 2. Fetch variables for the linked product family if available
            const isEmptyGuid = familyId && familyId.toString().toLowerCase() === '00000000-0000-0000-0000-000000000000';
            const selectedFamilyId = !isEmptyGuid && familyId
                ? familyId
                : (linkedFamilies?.length > 0 ? linkedFamilies[0].id : null);
            
            console.log('[GuiBuilder] Family selection debug:', {
                inputFamilyId: familyId,
                inputFamilyIdType: typeof familyId,
                isEmptyGuid: isEmptyGuid,
                linkedFamiliesCount: linkedFamilies?.length || 0,
                linkedFamiliesIds: linkedFamilies?.map(f => f.id) || [],
                selectedFamilyId: selectedFamilyId,
                selectedFamilyIdType: typeof selectedFamilyId
            });
            
            if (selectedFamilyId) {
                // Validate familyId format before API call
                const familyIdStr = String(selectedFamilyId);
                const isValidGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(familyIdStr);
                
                console.log(`[GuiBuilder] Family ID validation:`, {
                    familyId: familyIdStr,
                    isValidGuidFormat: isValidGuid,
                    isNotEmpty: familyIdStr !== '00000000-0000-0000-0000-000000000000'
                });
                
                if (!isValidGuid || familyIdStr.toLowerCase() === '00000000-0000-0000-0000-000000000000') {
                    console.error('[GuiBuilder] Invalid family ID format:', familyIdStr);
                } else {
                    const apiUrl = `/api/products/${familyIdStr}/features`;
                    console.log(`[GuiBuilder] Fetching features from: ${apiUrl}`);
                    
                    const varsRes = await apiFetch(apiUrl);
                    console.log(`[GuiBuilder] API Response:`, {
                        ok: varsRes.ok,
                        status: varsRes.status,
                        statusText: varsRes.statusText,
                        headers: Object.fromEntries(varsRes.headers.entries())
                    });
                    
                    if (varsRes.ok) {
                        const vars = await varsRes.json();
                        console.log('[GuiBuilder] Features loaded:', {
                            count: Array.isArray(vars) ? vars.length : Object.keys(vars || {}).length,
                            features: vars
                        });
                        setSourceFeatures(Array.isArray(vars) ? vars : []);
                        const ensureRes = await apiFetch(`/api/projects/${projectId}/presentation-sets/ensure`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ productFamilyId: familyIdStr, name: 'Standard' })
                        });
                        if (ensureRes.ok) {
                            const presentationSet = await ensureRes.json();
                            setPresentationSetId(current => current || presentationSet.id);
                            setCatalogContent(presentationSet.content?.features ? presentationSet.content : { features: {} });
                        }
                    } else {
                        const errorText = await varsRes.text();
                        console.error('[GuiBuilder] Failed to load features:', {
                            url: apiUrl,
                            status: varsRes.status,
                            error: errorText
                        });
                        
                        // Try to log to backend via a dedicated endpoint
                        const errorLog = {
                            error: 'Failed to load features in GuiBuilder',
                            familyId: familyIdStr,
                            apiUrl: apiUrl,
                            responseStatus: varsRes.status,
                            responseText: errorText,
                            timestamp: new Date().toISOString(),
                            userAgent: navigator.userAgent,
                            location: window.location.href
                        };
                        
                        // Try common backend logging endpoints
                        const logEndpoints = [
                            '/api/logs/frontend-error',
                            '/api/logs/client-error',
                            '/api/errors/log',
                            '/api/debug/log'
                        ];
                        
                        for (const endpoint of logEndpoints) {
                            try {
                                const logRes = await apiFetch(endpoint, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(errorLog)
                                });
                                if (logRes.ok) {
                                    console.log('[GuiBuilder] Error logged to backend:', endpoint);
                                    break; // Successfully logged, stop trying other endpoints
                                }
                            } catch {
                                // Try next endpoint
                                continue;
                            }
                        }
                    }
                }
            } else {
                console.warn('[GuiBuilder] No family selected - cannot load features. Ensure deployment has a valid productFamilyId or project has linked families.');
            }
        } catch (err) {
            console.error('Failed to load GUI builder data', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log('GuiBuilder: familyId changed to:', familyId, 'linkedFamilies:', linkedFamilies);
        setVersionTag(initialVersionTag);
        loadData();
    }, [projectId, initialVersionTag, familyId, linkedFamilies]);

    useEffect(() => {
        const familyIdStr = String(familyId || '');
        const isValidGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(familyIdStr);
        if (!isValidGuid || familyIdStr.toLowerCase() === '00000000-0000-0000-0000-000000000000') {
            setPreviewAvailableDomains(null);
            return undefined;
        }

        let cancelled = false;
        apiFetch(`/api/products/${familyIdStr}/evaluate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Selections: previewSelections })
        })
            .then(res => {
                if (!res.ok) throw new Error(`Evaluate failed (${res.status})`);
                return res.json();
            })
            .then(data => {
                if (!cancelled) {
                    setPreviewAvailableDomains(data.availableDomains || {});
                }
            })
            .catch(err => console.error(err));

        return () => {
            cancelled = true;
        };
    }, [familyId, previewSelections]);

    const previewSystemSelections = useMemo(
        () => deriveSystemSelections(features, previewSelections, previewAvailableDomains),
        [features, previewSelections, previewAvailableDomains]
    );
    const displayPreviewSelections = useMemo(
        () => displaySelections(previewSelections, previewSystemSelections),
        [previewSelections, previewSystemSelections]
    );

    // Clean up polling on unmount
    useEffect(() => {
        return () => {
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
            }
        };
    }, []);

    // Auto-scroll chat to bottom
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatMessages, chatLoading]);

    // Initialize chat session when preview modal opens
    useEffect(() => {
        if (showPreviewModal && generatedPayload && chatMessages.length === 0) {
            setChatMessages([
                { role: 'assistant', text: 'I can tune the same properties you see in Controls: accent, background, text color, font, rounding, shadow, padding, and spacing. The layout template stays as-is.' }
            ]);
            setModalViewMode('preview'); // Default to preview so the user sees results immediately
        }
    }, [showPreviewModal, generatedPayload, chatMessages.length]);

    // Sync manual styles state whenever generatedPayload changes or preview modal opens
    useEffect(() => {
        if (showPreviewModal && generatedPayload?.customCss) {
            setManualStyles(extractStyleTokensFromCss(generatedPayload.customCss));
        }
    }, [showPreviewModal, generatedPayload?.customCss]);

    useEffect(() => {
        if (!showPreviewModal) {
            setManualStyles(extractStyleTokensFromCss(properties.customCss));
        }
    }, [showPreviewModal, properties.customCss]);

    const handleManualStyleChange = (key, value) => {
        applyStyleTweaks({ [key]: value });
    };

    const applyStyleTweaks = (tweaks, { recordHistory = false } = {}) => {
        const sourceCss = generatedPayload?.customCss ?? properties.customCss ?? '';

        if (recordHistory && generatedPayload) {
            styleHistoryRef.current.push(sourceCss);
        }

        const css = applyStyleTweaksToCss(sourceCss, tweaks);
        const nextStyles = extractStyleTokensFromCss(css);
        setManualStyles(nextStyles);

        if (generatedPayload) {
            setGeneratedPayload({
                ...generatedPayload,
                customCss: css
            });
        } else {
            setProperties(prev => {
                const nextEmbed = tweaks.pageBackground
                    ? normalizeEmbedSettings({
                        ...prev.embed,
                        pageBackground: tweaks.pageBackground === 'transparent'
                            ? prev.embed?.pageBackground || DEFAULT_EMBED_SETTINGS.pageBackground
                            : tweaks.pageBackground,
                        transparent: tweaks.pageBackground === 'transparent'
                    })
                    : normalizeEmbedSettings(prev.embed);

                return {
                    ...prev,
                    customCss: css,
                    embed: nextEmbed,
                    theme: buildThemeFromTokens(nextStyles, nextEmbed)
                };
            });
        }
    };

    const handleEmbedChange = (keyOrPatch, value) => {
        const patch = typeof keyOrPatch === 'object' && keyOrPatch !== null
            ? keyOrPatch
            : { [keyOrPatch]: value };

        setProperties(prev => {
            const nextEmbed = normalizeEmbedSettings({ ...prev.embed, ...patch });
            const shouldSyncCanvas = patch.pageBackground !== undefined || patch.transparent !== undefined;
            const nextCss = shouldSyncCanvas
                ? applyStyleTweaksToCss(prev.customCss || '', { pageBackground: getEmbedPageBackground(nextEmbed) })
                : prev.customCss;
            const nextStyles = extractStyleTokensFromCss(nextCss);
            if (shouldSyncCanvas) {
                setManualStyles(nextStyles);
            }

            return {
                ...prev,
                embed: nextEmbed,
                customCss: nextCss,
                theme: buildThemeFromTokens(nextStyles, nextEmbed)
            };
        });
    };


    const handleSave = async () => {
        setSaving(true);
        try {
            const normalizedEmbed = normalizeEmbedSettings(properties.embed);
            const customCss = applyStyleTweaksToCss(properties.customCss || '', {
                pageBackground: getEmbedPageBackground(normalizedEmbed)
            });
            const styleTokens = extractStyleTokensFromCss(customCss);
            const payloadProperties = {
                ...properties,
                customCss,
                embed: normalizedEmbed,
                theme: normalizeTheme(properties.theme, normalizedEmbed, styleTokens)
            };
            setProperties(payloadProperties);

            const res = await apiFetch(`/api/projects/${projectId}/gui-schema`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    versionTag: versionTag,
                    isPublished: isPublished,
                    presentationSetId,
                    designPayload: {
                        pages: pages,
                        properties: payloadProperties,
                        presentationOverrides
                    }
                })
            });

            if (res.ok) {
                savedDesignRef.current = designSnapshot(pages, payloadProperties, presentationOverrides);
                alert('Layout saved successfully.');
                onLayoutSaved?.({ versionTag, isPublished });
            } else {
                alert('Failed to save layout.');
            }
        } catch (err) {
            console.error('Error saving layout schema', err);
        } finally {
            setSaving(false);
        }
    };

    const handleStartDesignJob = async () => {
        if (!targetUrl && !aiPrompt) {
            alert('Please provide a target URL to scrape, or custom AI prompt details.');
            return;
        }

        setIsGenerating(true);
        setGenerationStatus('Queuing design generation task...');
        
        try {
            const res = await apiFetch(`/api/projects/${projectId}/design-jobs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    targetUrl: targetUrl,
                    prompt: aiPrompt,
                    templateType: properties.layoutTemplate || 'split-70-30'
                })
            });

            if (res.ok) {
                const data = await res.json();
                pollJobStatus(data.id || data.jobId);
            } else {
                const errText = await res.text();
                alert('Failed to launch generation job: ' + errText);
                setIsGenerating(false);
            }
        } catch (err) {
            console.error('Error starting design job', err);
            alert('Failed to communicate with CaaS API.');
            setIsGenerating(false);
        }
    };

    const pollJobStatus = async (jobId) => {
        try {
            const res = await apiFetch(`/api/projects/${projectId}/design-jobs/${jobId}`);
            if (res.ok) {
                const job = await res.json();
                
                if (job.status === 'Pending') {
                    setGenerationStatus('Pending: Waiting in sequential queue...');
                    pollingTimeoutRef.current = setTimeout(() => pollJobStatus(jobId), 2000);
                } else if (job.status === 'Scraping') {
                    setGenerationStatus('Phase 1: Scraping target layout & visual styles (Playwright)...');
                    pollingTimeoutRef.current = setTimeout(() => pollJobStatus(jobId), 2000);
                } else if (job.status === 'Inference') {
                    setGenerationStatus('Phase 3: Restructuring layout config with AI (Ollama)...');
                    pollingTimeoutRef.current = setTimeout(() => pollJobStatus(jobId), 2000);
                } else if (job.status === 'Completed') {
                    setIsGenerating(false);
                    setGenerationStatus('');
                    if (job.resultPayload) {
                        setGeneratedPayload(job.resultPayload);
                        if (job.errorMessage) {
                            setChatMessages([
                                { role: 'assistant', text: job.errorMessage }
                            ]);
                        }
                        setShowIntroFrontPage(true);
                        setShowPreviewModal(true);
                    } else {
                        alert('Job completed but returned empty design parameters.');
                    }
                } else if (job.status === 'Failed') {
                    setIsGenerating(false);
                    setGenerationStatus('');
                    alert('Design generation failed: ' + (job.errorMessage || 'Unknown error'));
                }
            } else {
                pollingTimeoutRef.current = setTimeout(() => pollJobStatus(jobId), 3000);
            }
        } catch (err) {
            console.error('Error polling design job', err);
            pollingTimeoutRef.current = setTimeout(() => pollJobStatus(jobId), 3000);
        }
    };

    const handleSendChatMessage = async (e) => {
        if (e) e.preventDefault();
        if (!chatInput.trim() || chatLoading) return;

        const userText = chatInput;
        setChatInput('');
        setChatMessages(prev => [...prev, { role: 'user', text: userText }]);
        setChatLoading(true);

        try {
            if (isRevertStylePrompt(userText)) {
                const previousCss = styleHistoryRef.current.pop();
                if (!previousCss) {
                    setChatMessages(prev => [...prev, { role: 'assistant', text: 'There is no previous style tweak to revert.' }]);
                    return;
                }

                setGeneratedPayload({
                    ...generatedPayload,
                    customCss: previousCss
                });
                setManualStyles(extractStyleTokensFromCss(previousCss));
                setChatMessages(prev => [...prev, { role: 'assistant', text: 'Reverted the last style tweak.' }]);
                return;
            }

            const tweaks = inferStyleTweaksFromPrompt(userText, manualStyles);
            const summary = describeStyleTweaks(tweaks);

            if (!summary) {
                setChatMessages(prev => [
                    ...prev,
                    { role: 'assistant', text: 'I can adjust accent/background/text colors, font, edge rounding, shadows, padding, and feature spacing. Try "make the accent teal", "make the background transparent", "add a subtle shadow", or "undo".' }
                ]);
                return;
            }

            applyStyleTweaks(tweaks, { recordHistory: true });
            setChatMessages(prev => [
                ...prev,
                { role: 'assistant', text: `Updated ${summary}. You can fine-tune the same values in Controls.` }
            ]);
        } catch (err) {
            console.error('Error applying designer chat tweak', err);
            setChatMessages(prev => [...prev, { role: 'assistant', text: 'I could not apply that styling change.' }]);
        } finally {
            setChatLoading(false);
        }
    };

    // Page Management
    const addPage = () => {
        if (getFlowMode(properties.designSettings) === 'allInOne') {
            return;
        }
        const flowMode = getFlowMode(properties.designSettings);
        const titlePrefix = flowMode === 'tabs' ? 'Tab' : flowMode === 'accordions' ? 'Accordion' : 'Step';
        const newPage = {
            id: 'page-' + Date.now() + '-' + Math.random().toString(36).substr(2, 5),
            title: `${titlePrefix} ${pages.length + 1}`,
            description: 'Configure specifications for this step.',
            elements: []
        };
        setPages([...pages, newPage]);
        setActivePageIndex(pages.length);
    };

    const deletePage = (index) => {
        if (getFlowMode(properties.designSettings) === 'allInOne') {
            return;
        }
        if (pages.length <= 1) {
            alert('A configurator must have at least one page.');
            return;
        }
        if (!confirm('Are you sure you want to delete this page? All elements inside it will be removed.')) return;
        
        const updated = pages.filter((_, i) => i !== index);
        setPages(updated);
        setActivePageIndex(Math.max(0, index - 1));
    };

    const updatePageField = (index, field, value) => {
        const updated = [...pages];
        updated[index] = { ...updated[index], [field]: value };
        setPages(updated);
    };

    // Element Insertion (inside current page)
    const addHeader = () => {
        const newEl = {
            id: 'el-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
            type: 'header',
            text: 'Section Header'
        };
        const updated = [...pages];
        updated[activePageIndex].elements.push(newEl);
        setPages(updated);
    };

    const addDescription = () => {
        const newEl = {
            id: 'el-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
            type: 'description',
            text: 'Provide some helpful context or instructions here.'
        };
        const updated = [...pages];
        updated[activePageIndex].elements.push(newEl);
        setPages(updated);
    };

    const addExitButton = () => {
        // Allow one exit button across the layout
        const hasExit = pages.some(p => p.elements.some(el => el.type === 'exit-button'));
        if (hasExit) {
            alert('An exit button is already part of the layout.');
            return;
        }

        const newEl = {
            id: 'el-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
            type: 'exit-button',
            text: 'Exit Configurator'
        };
        const updated = [...pages];
        updated[activePageIndex].elements.push(newEl);
        setPages(updated);
    };

    // Reorder & modifications (within current page)
    const deleteElement = (id) => {
        const updated = [...pages];
        updated[activePageIndex].elements = updated[activePageIndex].elements.filter(el => el.id !== id);
        setPages(updated);
    };

    const moveElement = (index, direction) => {
        const targetIndex = index + direction;
        const pageElements = pages[activePageIndex].elements;
        if (targetIndex < 0 || targetIndex >= pageElements.length) return;

        const updatedElements = [...pageElements];
        const [moved] = updatedElements.splice(index, 1);
        updatedElements.splice(targetIndex, 0, moved);

        const updated = [...pages];
        updated[activePageIndex].elements = updatedElements;
        setPages(updated);
    };

    const updateCatalogValue = (featureName, valueKey, patch, exclusiveDefault = false) => {
        if (!featureName || !valueKey) return;
        setCatalogDirty(true);
        setCatalogContent(current => {
            const existing = current.features?.[featureName] || {};
            const values = { ...(existing.values || {}) };
            if (exclusiveDefault) {
                Object.keys(values).forEach(key => {
                    values[key] = { ...values[key], isDefault: false };
                });
            }
            const previous = values[valueKey] || {};
            values[valueKey] = {
                ...previous,
                ...patch,
                ...(patch.propertyValues ? { propertyValues: { ...(previous.propertyValues || {}), ...patch.propertyValues } } : {})
            };
            return {
                ...current,
                features: {
                    ...current.features,
                    [featureName]: { ...existing, values }
                }
            };
        });
    };

    const updatePresentationOverride = (featureName, patch) => {
        if (!featureName) return;
        setPresentationOverrides(current => {
            const existing = current[featureName] || {};
            return {
                ...current,
                [featureName]: {
                    ...existing,
                    ...patch,
                    values: patch.values ? { ...(existing.values || {}), ...patch.values } : (existing.values || {})
                }
            };
        });
    };

    const updateElementField = (id, field, value) => {
        const updated = [...pages];
        updated[activePageIndex].elements = updated[activePageIndex].elements.map(el => 
            el.id === id ? { ...el, [field]: value } : el
        );
        setPages(updated);
    };

    // Mapping Block Management
    const moveBlock = (blockName, targetColumn, indexDirection = 0) => {
        const mapping = { ...properties.layoutMapping };
        
        // Find which column it is currently in
        let sourceColumn = '';
        if (mapping.column1.includes(blockName)) sourceColumn = 'column1';
        else if (mapping.column2.includes(blockName)) sourceColumn = 'column2';
        else return;

        if (sourceColumn !== targetColumn) {
            // Move between columns
            mapping[sourceColumn] = mapping[sourceColumn].filter(b => b !== blockName);
            mapping[targetColumn].push(blockName);
        } else if (indexDirection !== 0) {
            // Reorder inside the same column
            const list = [...mapping[sourceColumn]];
            const idx = list.indexOf(blockName);
            const targetIdx = idx + indexDirection;
            if (targetIdx >= 0 && targetIdx < list.length) {
                list.splice(idx, 1);
                list.splice(targetIdx, 0, blockName);
                mapping[sourceColumn] = list;
            }
        }

        setProperties({ ...properties, layoutMapping: mapping });
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
                <div className="loader"></div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', height: '100%' }} className="fade-in">
            {/* Stylesheet Injector block */}
            <style>{properties.customCss}</style>

            {/* Toolbar */}
            <Toolbar
                versionTag={versionTag}
                setVersionTag={setVersionTag}
                isPublished={isPublished}
                setIsPublished={setIsPublished}
                designerMode={designerMode}
                setDesignerMode={setDesignerMode}
                setPreviewPageIndex={setPreviewPageIndex}
                setPreviewSelections={setPreviewSelections}
                setShowIntroFrontPage={setShowIntroFrontPage}
                defaultPreviewSelections={selectionsFromDefaultValues(features)}
                handleSave={handleSave}
                saving={saving}
                overrideCount={countDesignOverrides(presentationOverrides, pages)}
            />

            <div style={{ display: 'flex', gap: '1rem', flex: 1, minHeight: '550px' }}>
                {designerMode === 'edit' && (
                    <Sidebar
                        openSidebarSections={openSidebarSections}
                        onToggleSidebarSection={toggleSidebarSection}
                        pages={pages}
                        properties={properties}
                        setProperties={setProperties}
                        targetUrl={targetUrl}
                        setTargetUrl={setTargetUrl}
                        aiPrompt={aiPrompt}
                        setAiPrompt={setAiPrompt}
                        isGenerating={isGenerating}
                        handleStartDesignJob={handleStartDesignJob}
                        generationStatus={generationStatus}
                        familyId={familyId}
                        familyName={familyName}
                        linkedFamilies={linkedFamilies}
                        addHeader={addHeader}
                        addDescription={addDescription}
                        addExitButton={addExitButton}
                        moveBlock={moveBlock}
                        features={features}
                    />
                )}
                <CanvasArea
                    isPreviewMode={designerMode === 'view'}
                    properties={properties}
                    pages={pages}
                    activePageIndex={activePageIndex}
                    setActivePageIndex={setActivePageIndex}
                    addPage={addPage}
                    deletePage={deletePage}
                    updatePageField={updatePageField}
                    isDragOverCanvas={isDragOverCanvas}
                    handleFeatureDraggedFromPalette={handleFeatureDraggedFromPalette}
                    handleDragOver={handleDragOver}
                    handleDragLeave={handleDragLeave}
                    features={features}
                    presentationOverrides={presentationOverrides}
                    updatePresentationOverride={updatePresentationOverride}
                    catalogContent={catalogContent}
                    updateCatalogValue={updateCatalogValue}
                    moveElement={moveElement}
                    deleteElement={deleteElement}
                    updateElementField={updateElementField}
                    previewPageIndex={previewPageIndex}
                    setPreviewPageIndex={setPreviewPageIndex}
                    previewSelections={displayPreviewSelections}
                    setPreviewSelections={setPreviewSelections}
                    previewAvailableDomains={previewAvailableDomains}
                    showIntroFrontPage={showIntroFrontPage}
                    setShowIntroFrontPage={setShowIntroFrontPage}
                />

                {designerMode === 'edit' && (
                    <StylingPanel
                        activeTab={activeStylingTab}
                        setActiveTab={setActiveStylingTab}
                        manualStyles={manualStyles}
                        onStyleChange={handleManualStyleChange}
                        properties={properties}
                        setProperties={setProperties}
                        setShowTemplatePicker={setShowTemplatePicker}
                        setPages={setPages}
                        setActivePageIndex={setActivePageIndex}
                        onEmbedChange={handleEmbedChange}
                    />
                )}
            </div>

            {/* PREVIEW & APPLY MODAL */}
            {showPreviewModal && generatedPayload && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.4)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '2rem'
                }}>
                    <div style={{
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)',
                        width: '95%',
                        maxWidth: '1200px',
                        height: '80vh',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        overflow: 'hidden'
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            padding: '1.25rem 1.5rem',
                            borderBottom: '1px solid var(--border)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: 'var(--bg-card)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <i className="fa-solid fa-wand-magic-sparkles" style={{ color: '#2563eb' }}></i>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Review Generated CSS stylesheet</h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => { setShowPreviewModal(false); setGeneratedPayload(null); setModalViewMode('css'); setChatMessages([]); setChatLoading(false); setChatInput(''); setModalSidebarTab('chat'); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--text-secondary)' }}
                            >
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
                            {/* Left Column (Content & Toggles) */}
                            <div style={{ display: 'flex', flex: 1, flexDirection: 'column', overflow: 'hidden', padding: '1.5rem', gap: '1rem', minWidth: 0 }}>
                                {/* View Mode Toggle */}
                                <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem', gap: '0.75rem' }}>
                                    <button
                                        type="button"
                                        style={{
                                            background: modalViewMode === 'css' ? 'var(--text-primary)' : 'transparent',
                                            color: modalViewMode === 'css' ? 'var(--bg-primary)' : 'var(--text-secondary)',
                                            border: '1px solid var(--border)',
                                            padding: '0.4rem 1rem',
                                            borderRadius: 'var(--radius)',
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                        onClick={() => setModalViewMode('css')}
                                    >
                                        <i className="fa-solid fa-code" style={{ marginRight: '0.5rem' }}></i> View CSS Code
                                    </button>
                                    <button
                                        type="button"
                                        style={{
                                            background: modalViewMode === 'preview' ? 'var(--text-primary)' : 'transparent',
                                            color: modalViewMode === 'preview' ? 'var(--bg-primary)' : 'var(--text-secondary)',
                                            border: '1px solid var(--border)',
                                            padding: '0.4rem 1rem',
                                            borderRadius: 'var(--radius)',
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                        onClick={() => setModalViewMode('preview')}
                                    >
                                        <i className="fa-solid fa-eye" style={{ marginRight: '0.5rem' }}></i> Live Preview
                                    </button>
                                </div>

                                {/* View Content */}
                                {modalViewMode === 'css' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                                        <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>GENERATED CUSTOM STYLES</label>
                                        <textarea
                                            className="textarea-field"
                                            style={{
                                                flex: 1,
                                                fontFamily: 'Consolas, monospace',
                                                fontSize: '0.8rem',
                                                background: 'var(--bg-primary)',
                                                border: '1px solid var(--border)',
                                                padding: '0.75rem',
                                                resize: 'none'
                                            }}
                                            value={generatedPayload.customCss || ''}
                                            readOnly
                                        />
                                    </div>
                                ) : (
                                    <div style={{ 
                                        flex: 1, 
                                        overflowY: 'auto', 
                                        background: 'var(--bg-primary)', 
                                        border: '1px solid var(--border)',
                                        borderRadius: 'var(--radius)',
                                        padding: '2rem',
                                        boxSizing: 'border-box'
                                    }}>
                                        {/* Temporarily inject the new generated stylesheet */}
                                        <style>{generatedPayload.customCss || ''}</style>
                                        <ConfiguratorPreviewLayout
                                            properties={properties}
                                            pages={pages}
                                            previewPageIndex={previewPageIndex}
                                            setPreviewPageIndex={setPreviewPageIndex}
                                            previewSelections={displayPreviewSelections}
                                            setPreviewSelections={setPreviewSelections}
                                            previewAvailableDomains={previewAvailableDomains}
                                            features={features}
                                            showIntroFrontPage={showIntroFrontPage}
                                            setShowIntroFrontPage={setShowIntroFrontPage}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Right Column (Chat & Controls Panel) */}
                            <div style={{
                                width: '320px',
                                minWidth: '320px',
                                borderLeft: '1px solid var(--border)',
                                background: 'var(--bg-secondary)',
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                overflow: 'hidden',
                                boxSizing: 'border-box'
                            }}>
                                {/* Tabbed Selector */}
                                <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--bg-card)' }}>
                                    <button
                                        type="button"
                                        className={`item-highlight segment-tab${modalSidebarTab === 'chat' ? ' is-selected' : ''}`}
                                        onClick={() => setModalSidebarTab('chat')}
                                    >
                                        <i className="fa-solid fa-comments"></i> AI Chat
                                    </button>
                                    <button
                                        type="button"
                                        className={`item-highlight segment-tab${modalSidebarTab === 'manual' ? ' is-selected' : ''}`}
                                        onClick={() => setModalSidebarTab('manual')}
                                    >
                                        <i className="fa-solid fa-sliders"></i> Controls
                                    </button>
                                </div>

                                {modalSidebarTab === 'chat' ? (
                                    <>
                                        {/* Chat Messages List */}
                                        <div style={{
                                            flex: 1,
                                            overflowY: 'auto',
                                            padding: '1.25rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.75rem'
                                        }}>
                                            {chatMessages.map((msg, idx) => (
                                                <div 
                                                    key={idx} 
                                                    style={{
                                                        alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                                        background: msg.role === 'user' ? 'var(--accent, #001e62)' : 'var(--bg-primary)',
                                                        border: msg.role === 'user' ? 'none' : '1px solid var(--border)',
                                                        color: msg.role === 'user' ? '#ffffff' : 'var(--text-primary)',
                                                        padding: '0.6rem 0.85rem',
                                                        borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                                                        fontSize: '0.825rem',
                                                        maxWidth: '85%',
                                                        lineHeight: 1.4,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                    }}
                                                >
                                                    {msg.text}
                                                </div>
                                            ))}
                                            {chatLoading && (
                                                <div style={{
                                                    alignSelf: 'flex-start',
                                                    background: 'var(--bg-primary)',
                                                    border: '1px solid var(--border)',
                                                    color: 'var(--text-secondary)',
                                                    padding: '0.6rem 0.85rem',
                                                    borderRadius: '12px 12px 12px 2px',
                                                    fontSize: '0.825rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                                }}>
                                                    <i className="fa-solid fa-spinner fa-spin" style={{ color: '#2563eb' }}></i> Applying style tweak...
                                                </div>
                                            )}
                                            <div ref={chatEndRef} />
                                        </div>

                                        {/* Chat Input Area */}
                                        <form 
                                            onSubmit={handleSendChatMessage}
                                            style={{
                                                padding: '0.75rem 1.25rem 1.25rem 1.25rem',
                                                borderTop: '1px solid var(--border)',
                                                display: 'flex',
                                                gap: '0.5rem',
                                                background: 'var(--bg-card)',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <input
                                                type="text"
                                                className="input-field"
                                                placeholder={chatLoading ? "Applying..." : "e.g. Make buttons rounder..."}
                                                value={chatInput}
                                                onChange={e => setChatInput(e.target.value)}
                                                disabled={chatLoading}
                                                style={{
                                                    margin: 0,
                                                    padding: '0.5rem 0.75rem',
                                                    fontSize: '0.825rem',
                                                    borderRadius: 'var(--radius)',
                                                    flex: 1
                                                }}
                                            />
                                            <button
                                                type="submit"
                                                disabled={chatLoading || !chatInput.trim()}
                                                style={{
                                                    padding: '0.5rem 0.75rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: chatInput.trim() && !chatLoading ? 'var(--text-primary)' : 'var(--bg-secondary)',
                                                    color: chatInput.trim() && !chatLoading ? 'var(--bg-primary)' : 'var(--text-muted)',
                                                    border: '1px solid var(--border)',
                                                    borderRadius: 'var(--radius)',
                                                    cursor: chatInput.trim() && !chatLoading ? 'pointer' : 'not-allowed',
                                                    fontSize: '0.85rem',
                                                    transition: 'all 0.2s',
                                                    width: '36px',
                                                    height: '36px',
                                                    boxSizing: 'border-box'
                                                }}
                                                title="Send message"
                                            >
                                                <i className="fa-solid fa-paper-plane"></i>
                                            </button>
                                        </form>
                                    </>
                                ) : (
                                    /* ================= MANUAL CONTROLS FORM ================= */
                                    <div style={{
                                        flex: 1,
                                        overflowY: 'auto',
                                        padding: '1.25rem',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '1.25rem',
                                        boxSizing: 'border-box'
                                    }}>
                                        {/* Typography Selection */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                <i className="fa-solid fa-font" style={{ marginRight: '0.4rem' }}></i> Font Family
                                            </label>
                                            <select
                                                className="input-field"
                                                value={manualStyles.fontFamily}
                                                onChange={(e) => handleManualStyleChange('fontFamily', e.target.value)}
                                                style={{ fontSize: '0.825rem', padding: '0.5rem', margin: 0, height: '36px' }}
                                            >
                                                <option value="Inter">Inter (Clean Sans)</option>
                                                <option value="system-ui">System UI (Modern)</option>
                                                <option value="Arial">Arial (Standard)</option>
                                                <option value="Roboto">Roboto (Neo-Grotesque)</option>
                                                <option value="Montserrat">Montserrat (Geometric)</option>
                                                <option value="Helvetica">Helvetica (Classic)</option>
                                                <option value="Georgia">Georgia (Serif)</option>
                                                <option value="Playfair Display">Playfair Display (Premium Serif)</option>
                                            </select>
                                        </div>

                                        {/* Primary Accent Color */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                <i className="fa-solid fa-palette" style={{ marginRight: '0.4rem', color: '#2563eb' }}></i> Primary Accent
                                            </label>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <div style={{
                                                    position: 'relative',
                                                    width: '36px',
                                                    height: '36px',
                                                    borderRadius: '6px',
                                                    overflow: 'hidden',
                                                    border: '1px solid var(--border)',
                                                    flexShrink: 0
                                                }}>
                                                    <input
                                                        type="color"
                                                        value={colorInputValue(manualStyles.primaryColor, '#2563eb')}
                                                        onChange={(e) => handleManualStyleChange('primaryColor', e.target.value)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '-5px',
                                                            left: '-5px',
                                                            width: '46px',
                                                            height: '46px',
                                                            border: 'none',
                                                            padding: 0,
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                </div>
                                                <input
                                                    type="text"
                                                    className="input-field"
                                                    value={manualStyles.primaryColor}
                                                    onChange={(e) => handleManualStyleChange('primaryColor', e.target.value)}
                                                    style={{ margin: 0, padding: '0.4rem 0.6rem', fontSize: '0.825rem', height: '36px', boxSizing: 'border-box' }}
                                                    placeholder="#2563eb"
                                                />
                                            </div>
                                        </div>

                                        {/* Background Color */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                <i className="fa-solid fa-fill-drip" style={{ marginRight: '0.4rem' }}></i> Card/Input Background
                                            </label>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <div style={{
                                                    position: 'relative',
                                                    width: '36px',
                                                    height: '36px',
                                                    borderRadius: '6px',
                                                    overflow: 'hidden',
                                                    border: '1px solid var(--border)',
                                                    flexShrink: 0
                                                }}>
                                                    <input
                                                        type="color"
                                                        value={colorInputValue(manualStyles.backgroundColor, '#ffffff')}
                                                        onChange={(e) => handleManualStyleChange('backgroundColor', e.target.value)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '-5px',
                                                            left: '-5px',
                                                            width: '46px',
                                                            height: '46px',
                                                            border: 'none',
                                                            padding: 0,
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                </div>
                                                <input
                                                    type="text"
                                                    className="input-field"
                                                    value={manualStyles.backgroundColor}
                                                    onChange={(e) => handleManualStyleChange('backgroundColor', e.target.value)}
                                                    style={{ margin: 0, padding: '0.4rem 0.6rem', fontSize: '0.825rem', height: '36px', boxSizing: 'border-box' }}
                                                    placeholder="#ffffff"
                                                />
                                            </div>
                                        </div>

                                        {/* Text Color */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                <i className="fa-solid fa-font" style={{ marginRight: '0.4rem' }}></i> Text Color
                                            </label>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <div style={{
                                                    position: 'relative',
                                                    width: '36px',
                                                    height: '36px',
                                                    borderRadius: '6px',
                                                    overflow: 'hidden',
                                                    border: '1px solid var(--border)',
                                                    flexShrink: 0
                                                }}>
                                                    <input
                                                        type="color"
                                                        value={colorInputValue(manualStyles.textColor, '#1f2937')}
                                                        onChange={(e) => handleManualStyleChange('textColor', e.target.value)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: '-5px',
                                                            left: '-5px',
                                                            width: '46px',
                                                            height: '46px',
                                                            border: 'none',
                                                            padding: 0,
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                </div>
                                                <input
                                                    type="text"
                                                    className="input-field"
                                                    value={manualStyles.textColor}
                                                    onChange={(e) => handleManualStyleChange('textColor', e.target.value)}
                                                    style={{ margin: 0, padding: '0.4rem 0.6rem', fontSize: '0.825rem', height: '36px', boxSizing: 'border-box' }}
                                                    placeholder="#1f2937"
                                                />
                                            </div>
                                        </div>

                                        {/* Border Radius */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                    Border Radius
                                                </label>
                                                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{manualStyles.borderRadius}</span>
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="30"
                                                    value={parseNumber(manualStyles.borderRadius)}
                                                    onChange={(e) => handleManualStyleChange('borderRadius', e.target.value + 'px')}
                                                    style={{ flex: 1, accentColor: '#2563eb', cursor: 'pointer' }}
                                                />
                                                <input
                                                    type="text"
                                                    className="input-field"
                                                    value={manualStyles.borderRadius}
                                                    onChange={(e) => handleManualStyleChange('borderRadius', e.target.value)}
                                                    style={{ width: '65px', margin: 0, padding: '0.3rem 0.5rem', fontSize: '0.8rem', height: '28px', textAlign: 'center', boxSizing: 'border-box' }}
                                                />
                                            </div>
                                        </div>

                                        {/* Shadow */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                <i className="fa-solid fa-layer-group" style={{ marginRight: '0.4rem' }}></i> Shadow
                                            </label>
                                            <select
                                                className="input-field"
                                                value={manualStyles.shadow}
                                                onChange={(e) => handleManualStyleChange('shadow', e.target.value)}
                                                style={{ fontSize: '0.825rem', padding: '0.5rem', margin: 0, height: '36px' }}
                                            >
                                                {SHADOW_CONTROL_OPTIONS.map(option => (
                                                    <option key={option.label} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Input Padding */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                    Dropdown Padding
                                                </label>
                                                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{manualStyles.inputPadding}</span>
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <input
                                                    type="range"
                                                    min="4"
                                                    max="24"
                                                    value={parseNumber(manualStyles.inputPadding)}
                                                    onChange={(e) => {
                                                        const val = parseInt(e.target.value, 10);
                                                        handleManualStyleChange('inputPadding', `${val}px ${Math.round(val * 1.33)}px`);
                                                    }}
                                                    style={{ flex: 1, accentColor: '#2563eb', cursor: 'pointer' }}
                                                />
                                                <input
                                                    type="text"
                                                    className="input-field"
                                                    value={manualStyles.inputPadding}
                                                    onChange={(e) => handleManualStyleChange('inputPadding', e.target.value)}
                                                    style={{ width: '65px', margin: 0, padding: '0.3rem 0.5rem', fontSize: '0.8rem', height: '28px', textAlign: 'center', boxSizing: 'border-box' }}
                                                />
                                            </div>
                                        </div>

                                        {/* Button Padding */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                    Button Padding
                                                </label>
                                                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{manualStyles.buttonPadding}</span>
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <input
                                                    type="range"
                                                    min="6"
                                                    max="28"
                                                    value={parseNumber(manualStyles.buttonPadding)}
                                                    onChange={(e) => {
                                                        const val = parseInt(e.target.value, 10);
                                                        handleManualStyleChange('buttonPadding', `${val}px ${Math.round(val * 2)}px`);
                                                    }}
                                                    style={{ flex: 1, accentColor: '#2563eb', cursor: 'pointer' }}
                                                />
                                                <input
                                                    type="text"
                                                    className="input-field"
                                                    value={manualStyles.buttonPadding}
                                                    onChange={(e) => handleManualStyleChange('buttonPadding', e.target.value)}
                                                    style={{ width: '65px', margin: 0, padding: '0.3rem 0.5rem', fontSize: '0.8rem', height: '28px', textAlign: 'center', boxSizing: 'border-box' }}
                                                />
                                            </div>
                                        </div>

                                        {/* Group Spacing */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                    Feature Spacing
                                                </label>
                                                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{manualStyles.groupSpacing}</span>
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <input
                                                    type="range"
                                                    min="8"
                                                    max="48"
                                                    value={parseNumber(manualStyles.groupSpacing)}
                                                    onChange={(e) => handleManualStyleChange('groupSpacing', e.target.value + 'px')}
                                                    style={{ flex: 1, accentColor: '#2563eb', cursor: 'pointer' }}
                                                />
                                                <input
                                                    type="text"
                                                    className="input-field"
                                                    value={manualStyles.groupSpacing}
                                                    onChange={(e) => handleManualStyleChange('groupSpacing', e.target.value)}
                                                    style={{ width: '65px', margin: 0, padding: '0.3rem 0.5rem', fontSize: '0.8rem', height: '28px', textAlign: 'center', boxSizing: 'border-box' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div style={{
                            padding: '1rem 1.5rem',
                            borderTop: '1px solid var(--border)',
                            background: 'var(--bg-card)',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '0.75rem'
                        }}>
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => { setShowPreviewModal(false); setGeneratedPayload(null); setModalViewMode('css'); setChatMessages([]); setChatLoading(false); setChatInput(''); setModalSidebarTab('chat'); }}
                                style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                            >
                                Discard
                            </button>
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={() => {
                                    if (generatedPayload.customCss) {
                                        setProperties({
                                            ...properties,
                                            customCss: generatedPayload.customCss
                                        });
                                    }
                                    setShowPreviewModal(false);
                                    setGeneratedPayload(null);
                                    setModalViewMode('css');
                                    setChatMessages([]);
                                    setChatLoading(false);
                                    setChatInput('');
                                    setModalSidebarTab('chat');
                                }}
                                style={{ fontSize: '0.85rem', padding: '0.5rem 1rem', background: '#2563eb', color: '#ffffff' }}
                            >
                                Apply Stylesheet
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* TEMPLATE PICKER MODAL */}
            {showTemplatePicker && (
                <TemplatePicker
                    projectId={projectId}
                    onSelectTemplate={handleTemplateSelected}
                    onClose={() => setShowTemplatePicker(false)}
                />
            )}
        </div>
    );
}
